<template>
  <v-app-bar elevation="0" color="#F7F5EF" fixed height="72" class="site-nav">
    <v-container class="d-flex align-center pt-0 pb-0">
      <a href="/" class="link d-flex align-center">
        <!-- Plain img, not v-img, on purpose: Vuetify's v-img renders a
             loading-placeholder/loaded-image pair with a fade transition
             between them. That transition state is inherently different
             at build-time-prerender capture (settled, image loaded) vs. a
             fresh client mount (image not loaded yet) - a structural
             mismatch hydrate() can't reconcile, causing this component to
             be silently, entirely replaced during hydration. A plain img
             element has no such state and renders identically both times. -->
        <img src="/images/logo/logo-04.png" width="110" height="39" alt="migly"/>
      </a>
      <v-spacer/>
      <template v-if="!isMobileNav">
        <a href="/#recursos" class="nav-link">{{ $t('home.navHowItWorks') }}</a>
        <a href="/#bio-migly" class="nav-link">{{ $t('home.navBio') }}</a>
        <a href="/#pagina-de-vendas" class="nav-link">{{ $t('home.navSalesPage') }}<span class="nav-link-tag">{{ $t('comingSoon') }}</span></a>
        <a href="/#open-graph" class="nav-link">{{ $t('home.navLinkPreview') }}</a>
        <a href="/blog" class="nav-link">{{ $t('home.navBlog') }}</a>
        <a href="/#planos" class="nav-link">{{ $t('pricing') }}</a>
      </template>
      <template v-else>
        <!-- activator prop (not v-slot:activator) on purpose: the scoped-slot
             form makes Vuetify's Detachable mixin relocate this button's DOM
             node outside Vue's diffing on mount, which breaks hydration of
             the prerendered marketing routes (see public-page-render-handoff
             spec) - the id+selector form avoids that relocation entirely. -->
        <button id="site-nav-hamburger-activator" type="button" class="hamburger-btn" :aria-label="$t('home.openMenu')">
          <v-icon size="22" color="#101827">mdi-menu</v-icon>
        </button>
        <v-menu activator="#site-nav-hamburger-activator" offset-y>
          <v-list dense class="lang-menu">
            <v-list-item href="/#recursos">
              <v-list-item-title>{{ $t('home.navHowItWorks') }}</v-list-item-title>
            </v-list-item>
            <v-list-item href="/#bio-migly">
              <v-list-item-title>{{ $t('home.navBio') }}</v-list-item-title>
            </v-list-item>
            <v-list-item href="/#pagina-de-vendas">
              <v-list-item-title>{{ $t('home.navSalesPage') }}<span class="nav-link-tag">{{ $t('comingSoon') }}</span></v-list-item-title>
            </v-list-item>
            <v-list-item href="/#open-graph">
              <v-list-item-title>{{ $t('home.navLinkPreview') }}</v-list-item-title>
            </v-list-item>
            <v-list-item href="/blog">
              <v-list-item-title>{{ $t('home.navBlog') }}</v-list-item-title>
            </v-list-item>
            <v-list-item href="/#planos">
              <v-list-item-title>{{ $t('pricing') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
      <!-- activator prop, same reason as above -->
      <button id="site-nav-lang-activator" type="button" class="lang-switch" :aria-label="$t('home.languageSelector')">
        <country-flag :country="currentLocale === 'pt-br' ? 'br' : 'us'" size="small" class="lang-switch-flag"/>
        <span>{{ currentLocale === 'pt-br' ? 'PT' : 'EN' }}</span>
        <v-icon size="16" color="#5B6472">mdi-chevron-down</v-icon>
      </button>
      <v-menu activator="#site-nav-lang-activator" offset-y>
        <v-list dense class="lang-menu">
          <v-list-item @click="changeLocale('pt-br')" :class="{ 'lang-menu-item-active': currentLocale === 'pt-br' }">
            <country-flag country="br" size="small" class="mr-2"/>
            <v-list-item-title>Português</v-list-item-title>
          </v-list-item>
          <v-list-item @click="changeLocale('en')" :class="{ 'lang-menu-item-active': currentLocale === 'en' }">
            <country-flag country="us" size="small" class="mr-2"/>
            <v-list-item-title>English</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-btn v-if="isLogged" color="#101827" rounded depressed class="btn-migly btn-migly-dark text-none ml-2" @click="$router.push('/painel/links/shortened')">
        {{ $t('myAccount') }}
      </v-btn>
      <a v-else :href="loginHref" class="link ml-2">
        <v-btn color="#101827" rounded depressed class="btn-migly btn-migly-dark text-none">
          {{ $t('login') }}
        </v-btn>
      </a>
    </v-container>
  </v-app-bar>
</template>

<script>
import authSession from "../../../service/auth-session";
import localeService from "../../../service/locale";

export default {
  name: 'SiteHeader',
  mounted: function() {
    authSession.checkSession()
  },
  computed: {
    isLogged: function() {
      return authSession.state.authenticated
    },
    loginHref: function() {
      return `${process.env.VUE_APP_API_V1_URL}/oauth2/authorization/google`
    },
    currentLocale: function() {
      return this.$i18n.locale
    },
    isMobileNav: function() {
      return ['xs','sm'].includes(this.$vuetify.breakpoint.name)
    },
  },
  methods: {
    changeLocale: function(locale) {
      localeService.setLocale(locale)
    },
  },
}
</script>

<style scoped>
.site-nav {
  border-bottom: 1px solid rgba(16,24,39,0.08);
  font-family: 'Montserrat', sans-serif;
}
.nav-link {
  display: inline-block;
  position: relative;
  color: #101827;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  margin-right: 1.25rem;
  white-space: nowrap;
  transition: color 0.2s ease;
}
.nav-link::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -2px;
  width: 100%;
  height: 2px;
  background: #0277BD;
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s cubic-bezier(0.16,1,0.3,1);
}
.nav-link:hover {
  color: #0277BD;
}
.nav-link:hover::after {
  transform: scaleX(1);
}
.nav-link-tag {
  display: inline-flex;
  align-items: center;
  background: #0277BD;
  color: #FFFFFF;
  font-weight: 800;
  font-size: 0.55rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 0.1rem 0.35rem;
  border-radius: 999px;
  margin-left: 0.35rem;
  vertical-align: middle;
}

/*
 * O Vuetify tem uma regra global `[class*="text-"]` que colide com a classe
 * utilitária `text-none` (usada para remover o uppercase padrão) e
 * sobrescreve a cor do texto para um cinza-azulado quase invisível em
 * botões com fundo escuro. Forçamos branco explicitamente com ::v-deep
 * para não depender dessa cascata frágil.
 */
.btn-migly-dark ::v-deep .v-btn__content {
  color: #FFFFFF !important;
}
.btn-migly {
  transition: background-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}
.btn-migly-dark:hover {
  background-color: #0277BD !important;
  border-color: #0277BD !important;
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(16,24,39,0.3);
}
.lang-switch {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #FFFFFF;
  border: 1px solid rgba(16,24,39,0.1);
  border-radius: 999px;
  padding: 0.4rem 0.7rem;
  font-family: 'Montserrat', sans-serif;
  font-weight: 700;
  font-size: 0.78rem;
  color: #101827;
  cursor: pointer;
  transition: border-color 0.2s ease;
}
.lang-switch:hover {
  border-color: #0277BD;
}
.lang-switch-flag ::v-deep svg {
  width: 18px !important;
  height: 18px !important;
  border-radius: 50%;
}
.lang-menu {
  font-family: 'Montserrat', sans-serif;
}
.lang-menu-item-active ::v-deep .v-list-item__title {
  color: #0277BD;
  font-weight: 700;
}
.hamburger-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin-right: 0.4rem;
  border-radius: 999px;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
}
.hamburger-btn:hover {
  background: rgba(16,24,39,0.06);
}
</style>
